import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import get from "lodash/get";
import { AccessType } from "@quintype/components";

import { MemberDashboard } from "./member-dashboard/index";
import { NotFoundPage } from "./not-found/index";
import LoadingIndicatorComponent from "../atoms/loading-indicator";

const MemberDashboardWithAccessType = ({ member, getSubscriberMetadata, setSubscriberMetadata, isATGlobal, initAccessType, hasCheckedAuth, ...props }) => {
  const dispatch = useDispatch();
  const reduxSubscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const [subscriberMetadata, setImaSubscriberMetadata] = useState(null);

  useEffect(() => {
    // Use metadata from Redux (now globally fetched in TopBar)
    if (reduxSubscriberMetadata) {
      setImaSubscriberMetadata(reduxSubscriberMetadata);
    }
  }, [reduxSubscriberMetadata]);

  // Show loading while waiting for initial auth check
  if (!hasCheckedAuth) {
    return <LoadingIndicatorComponent />;
  }

  // Show NotFound page if member doesn't exist after auth check is complete
  if (!member) {
    return <NotFoundPage />;
  }

  // Show loading while waiting for subscriber metadata for logged-in users
  if (member && !subscriberMetadata) {
    return <LoadingIndicatorComponent />;
  }

  // Use actual member data from Redux store and AccessType metadata
  const {
    'first-name': firstName = '',
    'last-name': lastName = '',
    name: subscriberName = '',
    organisation: title = '',
    'membership-expiry': membershipExpiry = '',
    email: subscriberEmail = '',
    phone: subscriberPhone = '',
    location: city = '',
    'profile-image': profileImage = null,
    'primary-role': primaryRole,
  } = subscriberMetadata || {};

  const {
    name: memberName = '',
    email: memberEmail = '',
    'phone-number': memberPhone = '',
    'avatar-url': memberAvatar = null
  } = member || {};

  const combinedName = `${firstName} ${lastName}`.trim();
  const displayTitle = primaryRole !== 'super-user' ? `India ${primaryRole} Forum` : title;

  const profileData = {
    name: combinedName || subscriberName || memberName,
    title: displayTitle,
    membership: `Membership upto ${membershipExpiry}`,
    email: subscriberEmail || memberEmail,
    phone: subscriberPhone || memberPhone,
    city,
    profileImage: profileImage || memberAvatar
  };


  return (
    <MemberDashboard
      profileData={profileData}
      subscriberMetadata={subscriberMetadata}
      setSubscriberMetadata={setSubscriberMetadata}
      setImaSubscriberMetadata={setImaSubscriberMetadata}
      getSubscriberMetadata={getSubscriberMetadata}
      {...props}
    />
  );
};

const MemberDashboardPage = () => {
  const [isATGlobal, setIsATGlobal] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const member = useSelector((state) => get(state, ["member"], null));
  const email = get(member, ["email"], "");
  const phone = get(member, ["metadata", "phone-number"], "");
  const { key, accessTypeBkIntegrationId } = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "accesstypeConfig"], {})
  );

  // Set hasCheckedAuth to true after a short delay to allow initial auth check
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasCheckedAuth(true);
    }, 1000); // Give 1 second for auth check

    return () => clearTimeout(timer);
  }, []);

  return (
    <AccessType
      enableAccesstype={true}
      accessTypeKey={key}
      email={email}
      phone={phone}
      accessTypeBkIntegrationId={accessTypeBkIntegrationId}
      onATGlobalSet={() => {
        setIsATGlobal(true);
      }}
    >
      {({ initAccessType, getSubscriberMetadata, setSubscriberMetadata }) => (
        <MemberDashboardWithAccessType
          member={member}
          hasCheckedAuth={hasCheckedAuth}
          getSubscriberMetadata={getSubscriberMetadata}
          setSubscriberMetadata={setSubscriberMetadata}
          isATGlobal={isATGlobal}
          initAccessType={initAccessType}
        />
      )}
    </AccessType>
  );
};

export { MemberDashboardPage };